import isArray from 'lodash-es/isArray';

type Answers = Record<string, unknown>;

function isEmptyValue(value: unknown): boolean {
    return (
        value === null ||
        typeof value === 'undefined' ||
        value === '' ||
        (isArray(value) && value.length === 0)
    );
}

function equals(answer: unknown, value: unknown): boolean {
    if (isEmptyValue(answer)) {
        return false;
    }
    if (isArray(answer)) {
        return answer.some((item) => String(item) === String(value));
    }
    return String(answer) === String(value);
}

function compare(answer: unknown, value: unknown): number | null {
    if (isEmptyValue(answer) || isArray(answer)) {
        return null;
    }
    const answerNumber = Number(answer);
    const valueNumber = Number(value);
    if (!Number.isNaN(answerNumber) && !Number.isNaN(valueNumber)) {
        return answerNumber - valueNumber;
    }
    return String(answer).localeCompare(String(value));
}

function matchesText(
    answer: unknown,
    value: unknown,
    match: (answerText: string, valueText: string) => boolean,
): boolean {
    if (isEmptyValue(answer) || isArray(answer)) {
        return false;
    }
    return match(String(answer).toLowerCase(), String(value).toLowerCase());
}

function matchesFieldCondition({ field, operator, value }: FormFieldCondition, answers: Answers) {
    const answer = answers[field];
    const comparison = compare(answer, value);
    switch (operator) {
        case 'equals':
        case 'includes':
            return equals(answer, value);
        case 'not_equals':
        case 'excludes':
            return !equals(answer, value);
        case 'contains':
            return matchesText(answer, value, (answerText, valueText) =>
                answerText.includes(valueText),
            );
        case 'not_contains':
            return !matchesText(answer, value, (answerText, valueText) =>
                answerText.includes(valueText),
            );
        case 'starts_with':
            return matchesText(answer, value, (answerText, valueText) =>
                answerText.startsWith(valueText),
            );
        case 'ends_with':
            return matchesText(answer, value, (answerText, valueText) =>
                answerText.endsWith(valueText),
            );
        case 'lower_than':
            return comparison !== null && comparison < 0;
        case 'lower_than_or_equals':
            return comparison !== null && comparison <= 0;
        case 'greater_than':
            return comparison !== null && comparison > 0;
        case 'greater_than_or_equals':
            return comparison !== null && comparison >= 0;
        default:
            return false;
    }
}

/**
 * Whether a form condition is met by the answers, keyed by field name
 */
export default function matchesCondition(
    condition: FormCondition | null | undefined,
    answers: Answers,
): boolean {
    if (condition === null || typeof condition === 'undefined') {
        return true;
    }
    if ('all' in condition) {
        return condition.all.every((item) => matchesCondition(item, answers));
    }
    if ('any' in condition) {
        return condition.any.some((item) => matchesCondition(item, answers));
    }
    if ('not' in condition) {
        return !matchesCondition(condition.not, answers);
    }
    return matchesFieldCondition(condition, answers);
}
